import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Mail, MapPin, Phone } from 'lucide-react';
import { 
  FaSoundcloud, 
  FaBandcamp, 
  FaYoutube, 
  FaInstagram, 
  FaFacebook, 
  FaSpotify,
  FaGlobe 
} from 'react-icons/fa';
import { submitContact, getContactInfo, getSocialMedia } from '../services/api';
import { PageLayout } from './layouts/PageLayout';

const platformIcons = {
  soundcloud: FaSoundcloud,
  bandcamp: FaBandcamp,
  youtube: FaYoutube,
  instagram: FaInstagram,
  facebook: FaFacebook,
  spotify: FaSpotify,
  other: FaGlobe
};

const platformColors = {
  soundcloud: {
    icon: 'text-orange-500',
    iconHover: 'group-hover:text-orange-400',
    border: 'border-orange-500/30',
    borderHover: 'hover:border-orange-500',
    shadow: 'hover:shadow-orange-500/30',
    text: 'group-hover:text-orange-300'
  },
  bandcamp: {
    icon: 'text-cyan-500',
    iconHover: 'group-hover:text-cyan-400',
    border: 'border-cyan-500/30',
    borderHover: 'hover:border-cyan-500',
    shadow: 'hover:shadow-cyan-500/30',
    text: 'group-hover:text-cyan-300'
  },
  youtube: {
    icon: 'text-red-500',
    iconHover: 'group-hover:text-red-400',
    border: 'border-red-500/30',
    borderHover: 'hover:border-red-500',
    shadow: 'hover:shadow-red-500/30',
    text: 'group-hover:text-red-300'
  },
  instagram: {
    icon: 'text-pink-500',
    iconHover: 'group-hover:text-pink-400',
    border: 'border-pink-500/30',
    borderHover: 'hover:border-pink-500',
    shadow: 'hover:shadow-pink-500/30',
    text: 'group-hover:text-pink-300'
  },
  facebook: {
    icon: 'text-blue-500',
    iconHover: 'group-hover:text-blue-400',
    border: 'border-blue-500/30',
    borderHover: 'hover:border-blue-500',
    shadow: 'hover:shadow-blue-500/30',
    text: 'group-hover:text-blue-300'
  },
  spotify: {
    icon: 'text-green-500',
    iconHover: 'group-hover:text-green-400',
    border: 'border-green-500/30',
    borderHover: 'hover:border-green-500',
    shadow: 'hover:shadow-green-500/30',
    text: 'group-hover:text-green-300'
  },
  other: {
    icon: 'text-gray-500',
    iconHover: 'group-hover:text-gray-400',
    border: 'border-gray-500/30',
    borderHover: 'hover:border-gray-500',
    shadow: 'hover:shadow-gray-500/30',
    text: 'group-hover:text-gray-300'
  }
};

export const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [visibleElements, setVisibleElements] = useState([]);
  const [contactInfo, setContactInfo] = useState(null);
  const [socialMedia, setSocialMedia] = useState([]);

  useEffect(() => {
    fetchContactData();
  }, []);

  const fetchContactData = async () => {
    try {
      const [infoRes, socialRes] = await Promise.all([
        getContactInfo(),
        getSocialMedia()
      ]);
      setContactInfo(infoRes.data);
      setSocialMedia(socialRes.data.filter(s => s.active));
    } catch (error) {
      console.error('Failed to fetch contact data:', error);
    }
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleElements((prev) => [...new Set([...prev, entry.target.dataset.index])]);
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -100px 0px' }
    );

    const elements = document.querySelectorAll('[data-animate]');
    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      await submitContact(formData);
      setSubmitStatus('success');
      setFormData({ name: '', email: '', message: '' });
    } catch (error) {
      setSubmitStatus('error');
      console.error('Failed to submit contact form:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <PageLayout primaryColor="green" secondaryColor="orange">
      <div className="container mx-auto px-4 pt-20 md:pt-24 pb-16 md:pb-24 relative z-10">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div 
            className="text-center mb-12 md:mb-16 transition-all duration-700 ease-out"
            data-animate
            data-index="header"
            style={{
              opacity: visibleElements.includes('header') ? 1 : 0,
              transform: visibleElements.includes('header') ? 'translateY(0)' : 'translateY(30px)'
            }}
          >
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4 md:mb-6 text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-orange-400 hover:scale-105 transition-transform duration-300 cursor-default inline-block leading-tight pb-2">
              Get In Touch
            </h1>
            <div className="w-20 md:w-24 h-1 bg-gradient-to-r from-green-500 to-orange-500 mx-auto mb-4 md:mb-6"></div>
            <p className="text-base md:text-lg text-gray-400 hover:text-gray-300 transition-colors duration-300">
              We&apos;d love to hear from you
            </p>
          </div>

          {/* Contact Form - Full Width */}
          <div 
            className="bg-black/20 backdrop-blur-sm border border-green-500/30 rounded-lg p-6 md:p-8 hover:border-green-500 transition-all duration-300 mb-8"
            data-animate
            data-index="form"
            style={{
              opacity: visibleElements.includes('form') ? 1 : 0,
              transform: visibleElements.includes('form') ? 'translateY(0)' : 'translateY(30px)',
              transition: 'all 0.7s ease-out 0.2s'
            }}
          >
              <form onSubmit={handleSubmit} className="space-y-4 md:space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium mb-2 text-gray-300">
                    Name
                  </label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="bg-black/30 border-green-500/30 text-white focus:border-green-500 placeholder:text-gray-500"
                    placeholder="Your name"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium mb-2 text-gray-300">
                    Email
                  </label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="bg-black/30 border-green-500/30 text-white focus:border-green-500 placeholder:text-gray-500"
                    placeholder="your@email.com"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium mb-2 text-gray-300">
                    Message
                  </label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    className="bg-black/30 border-green-500/30 text-white focus:border-green-500 placeholder:text-gray-500"
                    placeholder="Your message..."
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-gradient-to-r from-green-500 to-orange-500 hover:from-green-600 hover:to-orange-600 text-white font-semibold py-3"
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </Button>

                {submitStatus === 'success' && (
                  <p className="text-green-400 text-center text-sm">Message sent successfully!</p>
                )}
                {submitStatus === 'error' && (
                  <p className="text-red-400 text-center text-sm">Failed to send message. Please try again.</p>
                )}
              </form>
            </div>

          {/* Contact Info - Below Form */}
          <div className="grid md:grid-cols-3 gap-6 md:gap-8 mb-12">
              {contactInfo && [
                { icon: Mail, title: 'Email', info: contactInfo.email, color: 'orange' },
                { icon: MapPin, title: 'Location', info: contactInfo.location, color: 'green' },
                { icon: Phone, title: 'Phone', info: contactInfo.phone, color: 'orange' }
              ].map((config) => {
                const Icon = config.icon;
                
                return (
                  <div
                    key={config.title}
                    data-animate
                    data-index={`info-${config.title}`}
                    className={`bg-black/20 backdrop-blur-sm border border-${config.color}-500/30 rounded-lg p-5 md:p-6 hover:border-${config.color}-500 transition-all duration-300 group`}
                    style={{
                      opacity: visibleElements.includes(`info-${config.title}`) ? 1 : 0,
                      transform: visibleElements.includes(`info-${config.title}`) ? 'translateX(0)' : 'translateX(30px)',
                      transition: `all 0.6s ease-out 0.3s`
                    }}
                  >
                    <div className="flex items-start space-x-4">
                      <div className={`w-10 h-10 md:w-12 md:h-12 rounded-full bg-${config.color}-500/20 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300`}>
                        <Icon className={`w-5 h-5 md:w-6 md:h-6 text-${config.color}-400`} />
                      </div>
                      <div>
                        <h3 className={`font-semibold mb-1 md:mb-2 text-sm md:text-base text-${config.color}-400`}>{config.title}</h3>
                        <p className="text-xs md:text-sm text-gray-400 group-hover:text-gray-300 transition-colors duration-300">{config.info}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
          </div>

          {/* Social Media Links */}
          {socialMedia.length > 0 && (
            <div className="mt-12">
              <div className="text-center mb-8">
                <h2 className="text-3xl md:text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-orange-400 mb-4">
                  Follow Us
                </h2>
                <div className="w-20 h-1 bg-gradient-to-r from-green-500 to-orange-500 mx-auto"></div>
              </div>

              <div className="flex flex-wrap justify-center gap-4">
                {socialMedia.map((social, idx) => {
                  const platformKey = social.platform.toLowerCase();
                  const Icon = platformIcons[platformKey] || FaGlobe;
                  const colors = platformColors[platformKey] || platformColors.other;
                  
                  return (
                    <a
                      key={social.id}
                      href={social.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group animate-fade-in"
                      style={{ animationDelay: `${idx * 0.1}s` }}
                    >
                      <div className={`flex items-center space-x-3 bg-black/20 backdrop-blur-sm border ${colors.border} ${colors.borderHover} rounded-lg px-6 py-4 hover:shadow-xl ${colors.shadow} hover:scale-110 transition-all duration-300`}>
                        <Icon className={`w-6 h-6 ${colors.icon} ${colors.iconHover} transition-colors duration-300`} />
                        <span className={`text-white font-semibold capitalize ${colors.text} transition-colors duration-300`}>
                          {social.platform}
                        </span>
                      </div>
                    </a>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </PageLayout>
  );
};
